import React, { Fragment, useEffect, useState, useContext, useMemo, useRef , useCallback} from 'react';
import '../Components/modal.css';
import './EditDatastream.css';
import AppDialog from '../Components/AppDialog';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../hooks/AuthProvider';
import ColorPicker from '../Components/ColorPicker';
import Select from 'react-select';
import { CheckCircle, ThumbUp, Cancel } from '@mui/icons-material';
import Handshake from '@mui/icons-material/Handshake';
import { LanguageContext } from '../context/LanguageContext';
import { useAppTheme } from '../context/ThemeContext';
import datastreamTranslations from '../languages/datastream.json';
import datastreamExtraTranslations from '../languages/datastreamExtra.json';
import { capitalize } from '@mui/material';
import { AddNewIcon, CustomSwitch, DescriptionIcon, NameIcon } from '../svg/UtilsIcons';
import { ActionIcon, ColorIcon, ConditionIcon, EventCodeIcon, EventTypeIcon, NotificationIcon } from '../svg/TemplateIcons';
import { getThemeColorValue, normalizeThemeMode } from '../utils/themeColors';
import {
    DATASTREAM_MIN_MAX_ABS_LIMIT,
    normalizeTemplateDatastream,
    normalizeTemplateDatastreamCollection,
    validateDatastreamMinMax,
} from '../utils/datastreamValidation';

const PinDropdown = ({ options, selectedPin, onSelectPin, disabled = false, translations }) => {
    const [searchTerm, setSearchTerm] = useState(selectedPin || '');
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (selectedPin !== searchTerm) {
            setSearchTerm(selectedPin || '');
        }
    }, [selectedPin]);

    const filteredOptions = useMemo(() => {
        return options.filter(option =>
            option.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [options, searchTerm]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleOptionClick = (option) => {
        if (!option.used && !disabled) {
            onSelectPin(option.value);
            setSearchTerm(option.value);
            setIsOpen(false);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setIsOpen(true);
        onSelectPin(value);
    };

    const handleInputFocus = () => {
        if (!disabled) {
            setIsOpen(true);
        }
    };

    return (
        <div className='pin-dropdown-container' ref={dropdownRef}>
            <input
                ref={inputRef}
                type='text'
                className='add-column'
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder={translations.placeholderSearchPin}
                disabled={disabled}

            />
            {isOpen && filteredOptions.length > 0 && (
                <ul
                    className='pin-dropdown-list'

                >
                    {filteredOptions.map((option) => (
                        <li
                            key={option.value}
                            className={`pin-dropdown-item ${option.used ? 'used' : ''} ${option.value === selectedPin ? 'selected' : ''}`}
                            onClick={() => handleOptionClick(option)}

                        >
                            <span>{option.name}</span>
                            {option.used && (
                                <span>
                                    ({translations.pinUsedBadge})
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const defaultValues = {
    temperature: {
        perfect: [20, 25],
        good: [18, 20],
        acceptable: [15, 18],
    },
    humidity: {
        perfect: [30, 60],
        good: [20, 30],
        acceptable: [15, 20],
    },
    CO2: {
        perfect: [400, 800],
        good: [801, 1000],
        acceptable: [1001, 1200],
    },
    VOC: {
        perfect: [0, 200],
        good: [201, 300],
        acceptable: [301, 400],
    },
    CH2O: {
        perfect: [0, 100],
        good: [101, 200],
        acceptable: [201, 300],
    },
    CO: {
        perfect: [0, 9],
        good: [10, 17],
        acceptable: [18, 34],
    },
    O3: {
        perfect: [0, 60],
        good: [61, 120],
        acceptable: [121, 180],
    },
    PM1: {
        perfect: [0, 12],
        good: [13, 35],
        acceptable: [36, 55],
    },
    PM25: {
        perfect: [0, 12],
        good: [13, 35],
        acceptable: [36, 55],
    },
    PM10: {
        perfect: [0, 54],
        good: [55, 154],
        acceptable: [155, 254],
    },
    NO2: {
        perfect: [0, 40],
        good: [41, 100],
        acceptable: [101, 200],
    },
};

const safeJsonParse = (jsonString, defaultValue = []) => {
    try {
        const parsed = JSON.parse(jsonString);
        return Array.isArray(parsed) ? parsed : defaultValue;
    } catch (e) {
        console.error("Failed to parse datastreams JSON:", e);
        return defaultValue;
    }
};

const isStandardName = (name) => {
    return ['perfect', 'good', 'acceptable'].includes(name.toLowerCase());
};

const CANONICAL_DATA_TYPE_OPTIONS = [
    { value: 'Integer', aliases: ['int', 'integer'] },
    { value: 'Double', aliases: ['double', 'float', 'number'] },
    { value: 'String', aliases: ['string', 'text'] },
];

const CANONICAL_INVALIDATE_UNIT_OPTIONS = [
    { value: 'sec', aliases: ['sec', 'second', 'seconds'] },
    { value: 'min', aliases: ['min', 'minute', 'minutes'] },
    { value: 'hour', aliases: ['hour', 'hours', 'hr'] },
];

const CANONICAL_THEN_SET_OPTIONS = [
    { value: 'no thing', aliases: ['nothing', 'no thing'] },
    { value: 'default', aliases: ['default'] },
    { value: 'No data', aliases: ['no data'] },
    { value: 'empty', aliases: ['empty'] },
    { value: '-', aliases: ['-'] },
];

const normalizeOptionToken = (value) => String(value ?? '').trim().toLowerCase();

const getLocalizedAliases = (translationKey, index) =>
    Object.values(datastreamTranslations)
        .map((locale) => locale?.[translationKey]?.[index])
        .filter(Boolean);

const resolveCanonicalOptionValue = (value, optionConfigs, translationKey) => {
    const normalizedValue = normalizeOptionToken(value);
    if (!normalizedValue) return '';

    for (let index = 0; index < optionConfigs.length; index += 1) {
        const option = optionConfigs[index];
        const aliases = [
            option.value,
            ...(option.aliases || []),
            ...getLocalizedAliases(translationKey, index),
        ];

        if (aliases.some((alias) => normalizeOptionToken(alias) === normalizedValue)) {
            return option.value;
        }
    }

    return typeof value === 'string' ? value : '';
};


export const EditDatastream = ({
    state,
    setState,
    datastream,
    setDatastream,
    templateDatastreams: rawTemplateDatastreams = [],
    templateEvaluationData,
    setTemplateEvaluationData,
    onDuplicate

}) => {
    const { lang } = useContext(LanguageContext);
    const { mode } = useAppTheme();
    const themeMode = normalizeThemeMode(mode);
    const t = {
        ...(datastreamTranslations[lang].EditDatastream || {}),
        ...(datastreamExtraTranslations[lang]?.EditDatastream || {}),
    };
    const { permissions } = useAuth();

    const canEditDatastream = permissions['edit_datastream'];


const resolverRef = useRef(null);
const [dialog, setDialog] = useState({
    open: false,
    type: 'alert', // 'alert' | 'confirm'
    variant: 'info', // 'info' | 'danger' | 'error'
    icon: 'alert', // 'alert' | 'delete'
    title: '',
    message: '',
    confirmText: t?.dialogOk || 'Ok',
    cancelText: t?.dialogCancel || 'Cancel',
    showCancel: false,
});

const closeDialog = useCallback((result = false) => {
    setDialog((d) => ({ ...d, open: false }));
    if (resolverRef.current) {
        resolverRef.current(result);
        resolverRef.current = null;
    }
}, []);

const alertDialog = useCallback(
    (opts) =>
        new Promise((resolve) => {
            resolverRef.current = resolve;

            setDialog({
                open: true,
                type: opts?.type || 'alert',
                variant: opts?.variant || 'info',
                icon: opts?.icon || 'alert',
                title: opts?.title || '',
                message: opts?.message || '',
                confirmText: opts?.confirmText || t?.dialogOk || 'Ok',
                cancelText: opts?.cancelText || t?.dialogCancel || 'Cancel',
                showCancel: Boolean(opts?.showCancel),
            });
        }),
    [t?.dialogOk, t?.dialogCancel]
);

    const tabs = datastreamTranslations[lang].tabs;
    const thenSets = datastreamTranslations[lang].thenSets;
    const invalidateUnits = datastreamTranslations[lang].invalidateUnits;
    const dataTypes = datastreamTranslations[lang].dataTypes;
    const titleText = state?.isNew ? (t.newTitle || 'New Datastream') : t.title;
    const resolveDataTypeValue = useCallback(
        (value) => resolveCanonicalOptionValue(value, CANONICAL_DATA_TYPE_OPTIONS, 'dataTypes'),
        []
    );
    const resolveInvalidateUnitValue = useCallback(
        (value) =>
            resolveCanonicalOptionValue(
                value,
                CANONICAL_INVALIDATE_UNIT_OPTIONS,
                'invalidateUnits'
            ),
        []
    );
    const resolveThenSetValue = useCallback(
        (value) => resolveCanonicalOptionValue(value, CANONICAL_THEN_SET_OPTIONS, 'thenSets'),
        []
    );
    const normalizeLocalizedDatastream = useCallback(
        (sourceDatastream) => {
            const normalized = normalizeTemplateDatastream(sourceDatastream) || {};

            return {
                ...normalized,
                dataType: resolveDataTypeValue(normalized?.dataType),
                invalidateUnit: resolveInvalidateUnitValue(normalized?.invalidateUnit),
                thenSet: resolveThenSetValue(normalized?.thenSet),
            };
        },
        [resolveDataTypeValue, resolveInvalidateUnitValue, resolveThenSetValue]
    );

    const [data, setData] = useState(() => normalizeLocalizedDatastream(datastream));
    const [currentTab, setCurrentTab] = useState(0);
    const [evaluations, setEvaluations] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({
        range: '',
        defaultValue: '',
    });
    const duplicateOnceRef = useRef(false);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const normalizeDataType = useCallback((value) => {
        if (value === null || value === undefined) return '';
        return String(value).trim().toLowerCase();
    }, []);

    const isNumericType = useMemo(() => {
        const dt = normalizeDataType(data.dataType);
        return dt === 'int' || dt === 'integer' || dt === 'double' || dt === 'float' || dt === 'number';
    }, [data.dataType, normalizeDataType]);

    const globalEvalNames = useMemo(() => templateEvaluationData?.evaluation_names || [], [templateEvaluationData?.evaluation_names]);
    const localRanges = useMemo(() => templateEvaluationData?.datastream_ranges || [], [templateEvaluationData?.datastream_ranges]);

    const [animateTab, setAnimateTab] = useState(true);

    useEffect(() => {
        setData(normalizeLocalizedDatastream(datastream));
    }, [datastream, normalizeLocalizedDatastream]);
    
    useEffect(() => {
        if (!datastream?.id || !globalEvalNames || globalEvalNames.length === 0) {
            setEvaluations([]);
            return;
        }

        const currentLocalRangesMap = new Map(
            (localRanges || [])
                .filter(r => r.datastream_id === datastream.id)
                .map(r => [r.template_datastream_evaluation_id, r])
        );

        const combinedEvaluations = globalEvalNames.map(globalEval => {
            const localRange = currentLocalRangesMap.get(globalEval.template_datastream_evaluation_id);
            const resolvedColor = getThemeColorValue(
                { ...globalEval, colorLight: globalEval?.colorLight ?? globalEval?.color_light },
                'color',
                themeMode,
                null
            );

            return {
                template_datastream_evaluation_id: globalEval.template_datastream_evaluation_id,
                name: globalEval.name,
                priority: globalEval.priority,
                color: resolvedColor,
                colorLight: globalEval?.colorLight ?? globalEval?.color_light ?? null,
                datastream_evaluation_id: localRange?.datastream_evaluation_id || null,
                datastream_id: datastream.id,
                from_value: localRange?.from_value ?? null,
                to_value: localRange?.to_value ?? null,
            };
        });

        setEvaluations(combinedEvaluations);
        const currentIndicator = 'custom';
        setData(prev => ({ ...prev, indicator: currentIndicator }));

    }, [datastream?.id, datastream?.indicator, datastream?.dataType, globalEvalNames, localRanges, themeMode]);



    const onChange = (e) => {
        const { name, value } = e.target;
        setData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'min' || name === 'max') {
            setFieldErrors(prev => ({ ...prev, range: '' }));
        }
        if (name === 'defaultValue') {
            setFieldErrors(prev => ({ ...prev, defaultValue: '' }));
        }
    };

    const canonicalDataType = resolveDataTypeValue(data.dataType);
    const canonicalInvalidateUnit = resolveInvalidateUnitValue(data.invalidateUnit);
    const canonicalThenSet = resolveThenSetValue(data.thenSet);
    const isStringDataType = canonicalDataType === 'String';
    const isIntDataType = canonicalDataType === 'Integer';
    const isDoubleDataType = canonicalDataType === 'Double';

    useEffect(() => {
        setFieldErrors({ range: '', defaultValue: '' });
    }, [data.id, data.dataType]);

    const decimalsSelectOptions = [
        { label: '#', value: 0 },
        { label: '#.#', value: 1 },
        { label: '#.##', value: 2 },
        { label: '#.###', value: 3 },
        { label: '#.####', value: 4 },
        { label: '#.#####', value: 5 },
        { label: '#.######', value: 6 },
    ];

    const parsedTemplateDatastreams = useMemo(() => {
        if (typeof rawTemplateDatastreams === 'string') {
            return normalizeTemplateDatastreamCollection(safeJsonParse(rawTemplateDatastreams));
        }
        return normalizeTemplateDatastreamCollection(rawTemplateDatastreams || []);
    }, [rawTemplateDatastreams]);

    const usedPins = useMemo(() => {
        return parsedTemplateDatastreams
            .filter(ds => state.mode !== 'edit' || ds.id !== data.id)
            .map(ds => ds.pin);
    }, [parsedTemplateDatastreams, data.id, state.mode]);

    const conflictingDatastream = useMemo(() => {
        const normalizedPin = data?.pin != null ? String(data.pin).trim() : '';
        if (!normalizedPin) return null;

        return parsedTemplateDatastreams.find((ds) => {
            if (!ds) return false;
            if (state.mode === 'edit' && String(ds.id) === String(data.id)) {
                return false;
            }
            return String(ds.pin ?? '').trim() === normalizedPin;
        }) || null;
    }, [parsedTemplateDatastreams, data?.id, data?.pin, state.mode]);

    const allVirtualPins = useMemo(() => {
        const pins = [];
        for (let i = 0; i < 200; i++) {
            pins.push(`V${i}`);
        }
        return pins;
    }, []);

    const pinOptions = useMemo(() => {
        return allVirtualPins.map(pin => ({
            name: pin,
            value: pin,
            used: usedPins.includes(pin)
        }));
    }, [allVirtualPins, usedPins]);

    const handlePinSelect = (pinValue) => {
        setData(prev => ({ ...prev, pin: pinValue }));
    };

    const isPinValid = useMemo(() => {
        if (!data.pin) return false;
        if (state.mode === 'edit' && datastream && data.pin === datastream.pin) {
            return true;
        }
        return !usedPins.includes(data.pin);
    }, [data.pin, usedPins, state.mode, datastream]);

    const pinConflictMessage = useMemo(() => {
        if (!conflictingDatastream) {
            return t.pinAlreadyUsed || 'This pin is already used.';
        }

        const pinLabel = String(data?.pin ?? '').trim();
        const datastreamLabel = String(
            conflictingDatastream?.name || conflictingDatastream?.pin || t.fallbackAnotherDatastream
        ).trim();

        return (t.pinAlreadyUsedByDatastream || t.pinAlreadyUsed || 'This pin is already used.')
            .replace('{pin}', pinLabel)
            .replace('{name}', datastreamLabel);
    }, [conflictingDatastream, data?.pin, t.fallbackAnotherDatastream, t.pinAlreadyUsed, t.pinAlreadyUsedByDatastream]);

    const handleEvaluationRangeChange = (globalId, field, value) => {
        setEvaluations(prevEvals => prevEvals.map(evalItem => {
            if (evalItem.template_datastream_evaluation_id === globalId) {
                const parsedValue = value !== '' ? parseFloat(value) : null;
                return {
                    ...evalItem,
                    [field]: isNaN(parsedValue) && value !== '' ? value : parsedValue,
                    datastream_id: datastream.id
                };
            }
            return evalItem;
        }));
    };


    useEffect(() => {
        setAnimateTab(false);
        const timer = setTimeout(() => setAnimateTab(true), 20);
        return () => clearTimeout(timer);
    }, [currentTab]);

    if (state.mode === 'edit' && !canEditDatastream) {
        return (
            <div id='modal' className='eds-scope'>

<AppDialog
    open={dialog.open}
    type={dialog.type}
    variant={dialog.variant}
    icon={dialog.icon}
    title={dialog.title}
    message={dialog.message}
    confirmText={dialog.confirmText}
    cancelText={dialog.cancelText}
    showCancel={dialog.showCancel}
    onConfirm={() => closeDialog(true)}
    onCancel={() => closeDialog(false)}
    onClose={() => closeDialog(false)}
/>

                <div
                    id='popup'

                >
                    <div className='modal-header'>
                        <h2>{t.permissionDeniedTitle}</h2>
                    </div>
                    <div className='modal-content'>
                        <p>{t.permissionDeniedMessage}</p>
                    </div>
                    <div className='modal-footer'>
                        <button className='btn btn-cancel'
                            onClick={() => setState((prev) => ({ ...prev, edit: false }))}>
                            {t.buttonClose}
                        </button>
                    </div>
                </div>
            </div>
        );
    }


    const handleSaveRanges = () => {
        const currentDatastreamId = data.id;

        const otherRanges = templateEvaluationData.datastream_ranges.filter(
            r => r.datastream_id !== currentDatastreamId
        );

        const newRanges = evaluations
            .filter(e => e.template_datastream_evaluation_id !== null)
            .filter(e => e.from_value !== null || e.to_value !== null)
            .map(e => ({
                datastream_evaluation_id: e.datastream_evaluation_id,
                datastream_id: currentDatastreamId,
                template_datastream_evaluation_id: e.template_datastream_evaluation_id,
                from_value: e.from_value,
                to_value: e.to_value,
            }));

        setTemplateEvaluationData(prevData => ({
            ...prevData,
            datastream_ranges: [...otherRanges, ...newRanges]
        }));
    };


    if (state.mode === 'edit' && !canEditDatastream) {
        return (
            <div id='modal' className='eds-scope'>

<AppDialog
    open={dialog.open}
    type={dialog.type}
    variant={dialog.variant}
    icon={dialog.icon}
    title={dialog.title}
    message={dialog.message}
    confirmText={dialog.confirmText}
    cancelText={dialog.cancelText}
    showCancel={dialog.showCancel}
    onConfirm={() => closeDialog(true)}
    onCancel={() => closeDialog(false)}
    onClose={() => closeDialog(false)}
/>

                <div
                    id='popup'

                >
                    <div className='modal-header'>
                        <h2>{t.permissionDeniedTitle}</h2>
                    </div>
                    <div className='modal-content'>
                        <p>{t.permissionDeniedMessage}</p>
                    </div>
                    <div className='modal-footer'>
                        <button className='btn btn-cancel'
                            onClick={() => setState((prev) => ({ ...prev, edit: false }))}>
                            {t.buttonClose}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const dataTypeSelectOptions = CANONICAL_DATA_TYPE_OPTIONS.map((option, index) => ({
        value: option.value,
        label: dataTypes[index] || option.value,
    }));
    const selectedDataTypeOption = dataTypeSelectOptions.find(
        (option) => option.value === canonicalDataType
    );

    const invalidateUnitSelectOptions = CANONICAL_INVALIDATE_UNIT_OPTIONS.map((option, index) => ({
        value: option.value,
        label: invalidateUnits[index] || option.value,
    }));
    const selectedInvalidateUnitOption = invalidateUnitSelectOptions.find(
        (option) => option.value === canonicalInvalidateUnit
    );

    const thenSetSelectOptions = CANONICAL_THEN_SET_OPTIONS.map((option, index) => ({
        value: option.value,
        label: thenSets[index] || option.value,
    }));
    const selectedThenSetOption = thenSetSelectOptions.find(
        (option) => option.value === canonicalThenSet
    );

    return (
        <div id='modal' className='eds-scope'>

<AppDialog
    open={dialog.open}
    type={dialog.type}
    variant={dialog.variant}
    icon={dialog.icon}
    title={dialog.title}
    message={dialog.message}
    confirmText={dialog.confirmText}
    cancelText={dialog.cancelText}
    showCancel={dialog.showCancel}
    onConfirm={() => closeDialog(true)}
    onCancel={() => closeDialog(false)}
    onClose={() => closeDialog(false)}
/>


            <div
                id='popup'
                className='dashboard-modal'

            >
                <div
                    className='modal-header'

                >
                    <div className="dashboard-header-icon">
                                                                    <AddNewIcon />
                                                                </div>
                    <div>
                        <h2

                        >
                            {titleText} {state.pinCategory ? `[${state.pinCategory}]` : null}
                        </h2>
                        <span

                        >
                            {t.subtitle}
                        </span>
                    </div>
                </div>
                <div className='modal-content'>
                    {state.mode === 'edit' ?
                        null :
                        <div
                            className='modal-info'

                        >
                            <InfoOutlined />
                            <div>
                                <h4>{t.viewModeTitle}</h4>
                                <p>
                                    {t.viewModeDescription}
                                </p>
                            </div>
                        </div>}
                    <div
                        className='details-tabs'

                    >
                        {tabs.map((tab, idx) => (
                            <button
                                key={idx}
                                type="button"
                                className={`details-tab ${idx === currentTab ? 'active' : ''}`}
                                onClick={() => setCurrentTab(idx)}
                                aria-pressed={idx === currentTab}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    <div
                        className={`tab-content-wrapper ${animateTab ? 'animate' : 'hidden'}`}

                    >
                    {tabs[currentTab] === datastreamTranslations[lang].tabs[1] ?
                        <Fragment>
                            <div
                                className='automation-alias-row'

                            >
                                <label

                                    className='form-lbl'
                                >
                                    {t.labelAutomationType}
                                </label>
                                <div>
                                    <div

                                    >
                                        <span role="img" aria-label="tag">
                                            🏷️
                                        </span>
                                    </div>
                                    {state.mode === 'edit' ? (
                                        <div>
                                            <input
                                                name='automationType'
                                                className='add-column'
                                                type='text'
                                                value={data.automationType || ''}
                                                maxLength={50}
                                                onChange={onChange}

                                            />
                                            <span

                                            >
                                                {(data.automationType || '').length}/50
                                            </span>
                                        </div>
                                    ) : (
                                        <div
                                            className='modal-info field'

                                        >
                                            <p>{data.automationType}</p>
                                            <span>
                                                {(data.automationType || '').length}/50
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div
                                className='automation-row'
                            >
                                <div>
                                    <div

                                    >
                                        <span role="img" aria-label="condition">
                                            📏
                                        </span>
                                    </div>
                                    <span>
                                        {t.labelCondition}
                                    </span>
                                </div>
                                {state.mode === 'edit' ? (
                                    <CustomSwitch
                                        checked={!!data.condition}
                                        onChange={(next) => setData({ ...data, condition: next ? 1 : 0 })}
                                    />
                                ) : (
                                    <CustomSwitch checked={!!data.condition} disabled />
                                )}
                            </div>

                            <div
                                className='automation-row'
                            >
                                <div>
                                    <div

                                    >
                                        <span role="img" aria-label="action">
                                            ⚙️
                                        </span>
                                    </div>
                                    <span>
                                        {t.labelAction}
                                    </span>
                                </div>
                                {state.mode === 'edit' ? (
                                    <CustomSwitch
                                        checked={!!data.action}
                                        onChange={(next) => setData({ ...data, action: next ? 1 : 0 })}
                                    />
                                ) : (
                                    <CustomSwitch checked={!!data.action} disabled />
                                )}
                            </div>
                        </Fragment> : (tabs[currentTab] === datastreamTranslations[lang].tabs[3] ?
                            <Fragment>
                                
                                {isNumericType && globalEvalNames.length > 0 ? (
                                    <>
                                        {evaluations
                                            .slice()
                                            .sort((a, b) => (a.priority || 100) - (b.priority || 100))
                                            .map((evalItem) => {
                                                const isStandardKey = isStandardName(evalItem.name);
                                                const isAutoFilled = data.indicator !== 'custom' && isStandardKey;

                                                let icon;
                                                switch (evalItem.name.toLowerCase()) {
                                                    case 'perfect': icon = <CheckCircle />; break;
                                                    case 'good': icon = <ThumbUp />; break;
                                                    case 'acceptable': icon = <Handshake />; break;
                                                    default: icon = <InfoOutlined />; break;
                                                }

                                                const currentFrom = evalItem.from_value ?? '';
                                                const currentTo = evalItem.to_value ?? '';

                                                return (
                                                    <div key={evalItem.template_datastream_evaluation_id} className='form-item'>
                                                        <div>
                                                            <div>
                                                                <span>
                                                                    {icon}
                                                                    {evalItem.color ? (
                                                                        <span
                                                                            aria-label="evaluation color"
                                                                            title={evalItem.color}

                                                                        />
                                                                    ) : null}

                                                                    <span>
                                                                        {evalItem.name}
                                                                        {state.mode !== 'edit' && evalItem.priority != null && evalItem.priority !== 100 && (
                                                                            <span>
                                                                                (P: {evalItem.priority})
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </span>
                                                            </div>

                                                            <div className='form-item'>
                                                                <label className='form-lbl'>{t.labelFrom}</label>
                                                                {state.mode === 'edit' ? (
                                                                    <input
                                                                        className='add-column'
                                                                        type='number'
                                                                        step='0.01'
                                                                        value={currentFrom}
                                                                        onChange={(e) => handleEvaluationRangeChange(evalItem.template_datastream_evaluation_id, 'from_value', e.target.value)}

                                                                    />
                                                                ) : (
                                                                    <div className='modal-info field'>
                                                                        <p>{currentFrom || '--'}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className='form-item'>
                                                                <label className='form-lbl'>{t.labelTo}</label>
                                                                {state.mode === 'edit' ? (
                                                                    <input
                                                                        className='add-column'
                                                                        type='number'
                                                                        step='0.01'
                                                                        value={currentTo}
                                                                        onChange={(e) => handleEvaluationRangeChange(evalItem.template_datastream_evaluation_id, 'to_value', e.target.value)}

                                                                    />
                                                                ) : (
                                                                    <div className='modal-info field'>
                                                                        <p>{currentTo || '--'}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                        <div key="bad" className='form-item'>
                                            <div>
                                                <div>
                                                    <Cancel />
                                                    <span>{t.bad || 'Bad'}</span>
                                                </div>
                                                <div>
                                                    {t.AnyValueOutsideTheAboveRangesIsConsideredBad}
                                                </div>
                                            </div>
                                        </div>

                                    </>
                                ) : (
                                    <div className='form-item'>
                                        <div className='modal-info'>
                                            <InfoOutlined />
                                            {globalEvalNames.length === 0 ? (
                                                <p>{t.evaluationOnlyForNumericNoNames}</p>
                                            ) : (
                                                <p>{t.evaluationOnlyForNumeric}</p>
                                            )}
                                            {state.mode === 'edit' && currentTab === 3 && (
                                                <p>
                                                    {t.changeDataTypeMessage}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Fragment>

                            : (tabs[currentTab] === datastreamTranslations[lang].tabs[0] ?
                                <Fragment>
                                    <div>
                                        <div className='form-item'>
                                            <label className='form-lbl'>{t.labelName}</label>
                                            {state.mode === 'edit' ?
                                                <input
                                                    name='name'
                                                    className='add-column'
                                                    type='text'
                                                    value={data.name}
                                                    onChange={onChange}

                                                /> :
                                                <div className='modal-info field'>
                                                    <p>{data.name}</p>
                                                </div>}
                                        </div>
                                        <div className='form-item'>
                                            <label className='form-lbl'>{t.labelAlias}</label>
                                            {state.mode === 'edit' ?
                                                <input
                                                    name='alias'
                                                    className='add-column'
                                                    type='text'
                                                    value={data.alias}
                                                    onChange={onChange}

                                                /> :
                                                <div className='modal-info field'>
                                                    <p>{data.alias}</p>
                                                </div>}
                                        </div>
                                    </div>
                                    <div className='form-item'>
                                        <label className='form-lbl'>{t.labelColor}</label>
                                        {state.mode === 'edit' ?
                                            <ColorPicker
                                                color={data.color}
                                                setColor={(c) => setData(prev => ({ ...prev, color: c }))}
                                            /> :
                                            <div className='color-picker'
></div>}
                                    </div>
                                    <div className='form-item'>
                                        <label className='form-lbl'>{t.labelPin}</label>
                                        {state.mode === 'edit' ?
                                            <PinDropdown
                                                options={pinOptions}
                                                selectedPin={data.pin}
                                                onSelectPin={handlePinSelect}
                                                disabled={false}
                                                translations={t}
                                            /> :
                                            <div className='modal-info field'>
                                                <p>{data.pin}</p>
                                            </div>}
                                        {!isPinValid && data.pin && (
                                            <p>{pinConflictMessage}</p>
                                        )}
                                    </div>
                                    <div className='form-item'>
                                        <label className='form-lbl'>{t.labelDataType}</label>
                                        {state.mode === 'edit' ?
                                            <Select
                                                id={'dataType'}
                                                options={dataTypeSelectOptions}
                                                value={selectedDataTypeOption}
                                                onChange={(selectedOption) => {
                                                    const nextType = selectedOption ? selectedOption.value : null;
                                                    setData((prev) => {
                                                        const next = { ...prev, dataType: nextType };
                                                        const dt = normalizeDataType(nextType);
                                                        const nextIsNumeric =
                                                            dt === 'int' ||
                                                            dt === 'integer' ||
                                                            dt === 'double' ||
                                                            dt === 'float' ||
                                                            dt === 'number';

                                                        if (!nextIsNumeric) {
                                                            next.min = '';
                                                            next.max = '';
                                                            next.decimals = '';
                                                        }

                                                        return next;
                                                    });
                                                }}
                                                classNamePrefix="react-select"
                                            /> :
                                            <div className='modal-info field'>
                                                <p>{selectedDataTypeOption?.label || data.dataType}</p>
                                            </div>}
                                    </div>
                                    <div className='form-item'>
                                        <label className='form-lbl'>{t.labelUnit}</label>
                                        {state.mode === 'edit' ?
                                            <input
                                                name='unit'
                                                className='add-column'
                                                type='text'
                                                value={data.unit}
                                                onChange={onChange}

                                                /> :
                                            <div className='modal-info field'>
                                                <p>{data.unit}</p>
                                            </div>}
                                    </div>
                                    <div className='form-item automation-row'>
                                        <label className='form-lbl'>{t.labelIsControlled}</label>
                                        {state.mode === 'edit' ? (
                                            <CustomSwitch
                                                checked={Boolean(data.isControlled)}
                                                onChange={(next) => setData((prev) => ({ ...prev, isControlled: Boolean(next) }))}
                                            />
                                        ) : (
                                            <CustomSwitch checked={Boolean(data.isControlled)} disabled />
                                        )}
                                    </div>
                                    {!isStringDataType && (
                                        <div className='eds-minmax-row'>
                                            <div className='form-item'>
                                                <label className='form-lbl'>{t.labelMin}</label>
                                                {state.mode === 'edit' ?
                                                    <input
                                                        name='min'
                                                        className={`add-column${fieldErrors.range ? ' eds-input-error' : ''}`}
                                                        type='text'
                                                        value={data.min}
                                                        onChange={onChange}

                                                    /> :
                                                    <div className='modal-info field'>
                                                        <p>{data.min}</p>
                                                    </div>}
                                            </div>

                                            <div className='form-item'>
                                                <label className='form-lbl'>{t.labelMax}</label>
                                                {state.mode === 'edit' ?
                                                    <input
                                                        name='max'
                                                        className={`add-column${fieldErrors.range ? ' eds-input-error' : ''}`}
                                                        type='text'
                                                        value={data.max}
                                                        onChange={onChange}

                                                    /> :
                                                    <div className='modal-info field'>
                                                        <p>{data.max}</p>
                                                    </div>}
                                            </div>
                                        </div>
                                    )}
                                    {fieldErrors.range ? (
                                        <p className='eds-field-error'>{fieldErrors.range}</p>
                                    ) : null}

                                    {isDoubleDataType && (
                                        <div className='form-item'>
                                            <label className='form-lbl'>{t.labelDecimals}</label>
                                            {state.mode === 'edit' ?
                                                <Select
                                                        id="decimals"
                                                        options={decimalsSelectOptions}
                                                        value={decimalsSelectOptions.find(o => o.value === data.decimals) || null}
                                                        onChange={selectedOption =>
                                                            setData({
                                                                ...data,
                                                                decimals: selectedOption ? selectedOption.value : null,
                                                            })
                                                        }
                                                        classNamePrefix="react-select"
                                                    />:
                                                <div className='modal-info field'>
                                                    <p>{data.decimals}</p>
                                                </div>}
                                        </div>
                                    )}
                                    <div className='form-item'>
                                        <label className='form-lbl'>{t.labelDefaultValue}</label>
                                        {state.mode === 'edit' ?
                                            <input
                                                name='defaultValue'
                                                className={`add-column${fieldErrors.defaultValue ? ' eds-input-error' : ''}`}
                                                type='text'
                                                value={data.defaultValue}
                                                onChange={onChange}

                                            /> :
                                            <div className='modal-info field'>
                                                <p>{data.defaultValue}</p>
                                            </div>}
                                        {fieldErrors.defaultValue ? (
                                            <p className='eds-field-error'>{fieldErrors.defaultValue}</p>
                                        ) : null}
                                    </div>
                                </Fragment> :
                                <Fragment>
                                    <div className='form-item automation-row' >
                                        <label className='form-lbl'>{t.labelEnableHistoryData}</label>
                                        {state.mode === 'edit' ? (
                                            <CustomSwitch
                                                checked={!!data.enableHistory}
                                                onChange={(next) => setData({ ...data, enableHistory: next ? 1 : 0 })}
                                            />
                                        ) : (
                                            <CustomSwitch checked={!!data.enableHistory} disabled />
                                        )}
                                    </div>
                                    {data.enableHistory ? <Fragment>

                                        <div className='form-item automation-row' >
                                            <label className='form-lbl'>{t.labelSaveRawData}</label>
                                            {state.mode === 'edit' ? (
                                                <CustomSwitch
                                                    checked={!!data.isRow}
                                                    onChange={(next) => setData({ ...data, isRow: next ? 1 : 0 })}
                                                />
                                            ) : (
                                                <CustomSwitch checked={!!data.isRow} disabled />
                                            )}
                                        </div>
                                        <div className='form-item automation-row' >
                                            <label className='form-lbl'>{t.labelShowInCustomChart}</label>
                                            {state.mode === 'edit' ? (
                                                <CustomSwitch
                                                    checked={!!data.customChart}
                                                    onChange={(next) => setData({ ...data, customChart: next ? 1 : 0 })}
                                                />
                                            ) : (
                                                <CustomSwitch checked={!!data.customChart} disabled />
                                            )}
                                        </div>
                                        <div className='form-item automation-row' >
                                            <label className='form-lbl'>{t.labelSync}</label>
                                            {state.mode === 'edit' ? (
                                                <CustomSwitch
                                                    checked={!!data.sync}
                                                    onChange={(next) => setData({ ...data, sync: next ? 1 : 0 })}
                                                />
                                            ) : (
                                                <CustomSwitch checked={!!data.sync} disabled />
                                            )}
                                        </div>
                                        <div className='form-item automation-row' >
                                            <label className='form-lbl'>{t.labelShowInReports}</label>
                                            {state.mode === 'edit' ? (
                                                <CustomSwitch
                                                    checked={!!data.reports}
                                                    onChange={(next) => setData({ ...data, reports: next ? 1 : 0 })}
                                                />
                                            ) : (
                                                <CustomSwitch checked={!!data.reports} disabled />
                                            )}
                                        </div>
                                        <div className='form-item automation-row'>
                                            <label className='form-lbl'>{t.labelInvalidate}</label>
                                            {state.mode === 'edit' ? (
                                                <CustomSwitch
                                                    checked={!!data.invalidate}
                                                    onChange={(next) => setData({ ...data, invalidate: next ? 1 : 0 })}
                                                />
                                            ) : (
                                                <CustomSwitch checked={!!data.invalidate} disabled />
                                            )}
                                        </div>
                                        {data.invalidate ?
                                            <Fragment>
                                                <div className='form-item'>
                                                    <label className='form-lbl'>{t.labelInvalidateValue}</label>
                                                    {state.mode === 'edit' ?
                                                        <input
                                                            name='invalidateValue'
                                                            className='add-column'
                                                            type='text'
                                                            value={data.invalidateValue}
                                                            onChange={onChange}

                                                        /> :
                                                        <div className='modal-info field'>
                                                            <p>{data.invalidateValue}</p>
                                                        </div>}
                                                </div>
                                                <div className='form-item'>
                                                    <label className='form-lbl'>{t.labelInvalidateUnit}</label>
                                                    {state.mode === 'edit' ?
                                                        <Select
                                                            id={'invalidateUnit'}
                                                            options={invalidateUnitSelectOptions}
                                                            value={selectedInvalidateUnitOption}
                                                            onChange={selectedOption => setData({ ...data, invalidateUnit: selectedOption ? selectedOption.value : null })}
                                                            classNamePrefix="react-select"
                                                        /> :
                                                        <div className='modal-info field'>
                                                            <p>{selectedInvalidateUnitOption?.label || data.invalidateUnit}</p>
                                                        </div>}
                                                </div>
                                                <div className='form-item'>
                                                    <label className='form-lbl'>{t.labelThenSet}</label>
                                                    {state.mode === 'edit' ?
                                                        <Select
                                                            id={'thenSet'}
                                                            options={thenSetSelectOptions}
                                                            value={selectedThenSetOption}
                                                            onChange={selectedOption => setData({ ...data, thenSet: selectedOption ? selectedOption.value : null })}
                                                            classNamePrefix="react-select"
                                                            menuPlacement="top"
                                                        /> :
                                                        <div className='modal-info field'>
                                                            <p>{selectedThenSetOption?.label || data.thenSet}</p>
                                                        </div>}
                                                </div>
                                            </Fragment>
                                            : null}
                                    </Fragment> : null}
                                </Fragment>)


                    )}
                    </div>
                </div>
                <div
                    className='modal-footer'

                >
                    <button
                        className='btn btn-cancel'

                        onClick={() => setState((prev) => ({ ...prev, edit: false, isNew: false }))}
                    >
                        {t.buttonCancel}
                    </button>
                    {state.mode === 'edit' && typeof onDuplicate === 'function' ? (
                        <button
                            type="button"
                            className='btn btn-duplicate'

                            disabled={isDuplicating}
                            onClick={async (e) => {
                                e?.preventDefault?.();
                                e?.stopPropagation?.();

                                if (duplicateOnceRef.current) return;
                                duplicateOnceRef.current = true;
                                setIsDuplicating(true);

                                try {
                                    await Promise.resolve(onDuplicate?.({ datastream: data, evaluations }));
                                } finally {
                                    setIsDuplicating(false);
                                    setState((prev) => ({ ...prev, edit: false }));
                                }
                            }}
                        >
                            {t.buttonDuplicate}
                        </button>
                    ) : null}


                    {state.mode === 'edit' ? (
                        <button
                            className='btn btn-apply'

                            onClick={async () => {

                                const pinValue = data.pin != null ? String(data.pin).trim() : '';
                                if (!pinValue) {
                                    await alertDialog({
                                        variant: 'error',
                                        title: t.dialogErrorTitle || 'Error',
                                        message: t.pinRequired,
                                        confirmText: t.dialogOk || 'Ok'
                                    });
                                    return;
                                }


                                if (!isPinValid) {
                                    await alertDialog({
                                        variant: 'error',
                                        title: t.dialogErrorTitle || 'Error',
                                        message: pinConflictMessage,
                                        confirmText: t.dialogOk || 'Ok'
                                    });
                                    return;
                                }

                                const minRaw = data.min;
                                const maxRaw = data.max;
                                const unitRaw = data.unit;

                                const unitValue = unitRaw != null ? String(unitRaw).trim() : '';
                                setFieldErrors({ range: '', defaultValue: '' });

                                const rangeValidation = validateDatastreamMinMax(
                                    {
                                        ...data,
                                        min: minRaw,
                                        max: maxRaw,
                                    },
                                    {
                                        minMaxRequired:
                                            t.minMaxRequired || t.minMaxUnitRequired,
                                        outOfBounds:
                                            (t.minMaxOutOfBounds || '')
                                                .replaceAll('{limit}', DATASTREAM_MIN_MAX_ABS_LIMIT.toLocaleString()),
                                        minLessThanMax: t.minLessThanMax,
                                        defaultInvalid: t.defaultValueInvalid,
                                        defaultOutOfRange: t.defaultValueOutOfRange,
                                    }
                                );

                                if (!rangeValidation.valid) {
                                    if (rangeValidation.field === 'defaultValue') {
                                        setFieldErrors((prev) => ({ ...prev, defaultValue: rangeValidation.message }));
                                    } else {
                                        setFieldErrors((prev) => ({ ...prev, range: rangeValidation.message }));
                                    }
                                    return;
                                }

                                const defaultValueParsed = isNumericType
                                    ? rangeValidation.normalized.defaultValue
                                    : data.defaultValue;

                                setDatastream({
                                    ...data,
                                    unit: unitValue,
                                    isControlled: Boolean(data.isControlled),
                                    indicator: isNumericType ? data.indicator : null,
                                    min: isNumericType ? rangeValidation.normalized.min : null,
                                    max: isNumericType ? rangeValidation.normalized.max : null,
                                    defaultValue: defaultValueParsed
                                });

                                handleSaveRanges();

                                setState((prev) => ({ ...prev, edit: false, isNew: false }));
                            }}
                        >
                            {t.buttonApply}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const typeOption = ['info', 'warning', 'critical', 'content'];
export const EditEvent = ({ close, mode, event, setEvent }) => {
    const { permissions } = useAuth();
    const { lang } = useContext(LanguageContext);

    const canViewDeviceEvents = permissions['view_device_events'];

    const t = {
        ...(datastreamTranslations[lang].EditEvent || {}),
        ...(datastreamExtraTranslations[lang]?.EditEvent || {}),
    };
    const [currentEvent, setCurrentEvent] = useState(event);

    const onChange = (e) =>
        setCurrentEvent(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const titleText =
        mode === 'add'
            ? t.labelNewEvent
            : mode === 'view'
                ? t.labelEventDetails
                : t.labelEditEvent;

    const eventTypeOptions = typeOption.map(type => ({ value: type, label: type }));
    const selectedEventTypeOption = eventTypeOptions.find(
        option => option.value === currentEvent.type
    );

    const nameLength = (currentEvent.name || '').length;
    const codeLength = (currentEvent.code || '').length;

    if (!canViewDeviceEvents) {
        return (
            <div id="modal" className="eds-scope">
                <div
                    id="popup"

                >
                    <div className="modal-header">
                        <h2>{t.permissionDeniedTitle}</h2>
                    </div>
                    <div className="modal-content">
                        <p>{t.permissionDeniedMessage}</p>
                    </div>
                    <div
                        className="modal-footer"

                    >
                        <button className="btn btn-cancel" onClick={close}>
                            {t.buttonClose}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="modal" className="eds-scope">
            <div
                id="popup"
                className="dashboard-modal"

            >
                <div
                    className="modal-header"

                >
                    <div className="dashboard-header-icon">
                        <AddNewIcon />
                    </div>
                    <div>
                        <h2

                        >
                            {titleText}
                        </h2>
                        <span

                        >
                            {t.subtitle}
                        </span>
                    </div>
                </div>

                <div className="modal-content">
                    <div>
                        <div className="form-item">
                            <label
                                className="form-lbl"

                            >
                                <NameIcon />
                                {t.labelName}
                            </label>
                            {mode !== 'view' ? (
                                <div>
                                    <input
                                        name="name"
                                        className="add-column"
                                        type="text"
                                        maxLength={50}
                                        value={currentEvent.name ?? ''}
                                        onChange={onChange}
                                        placeholder={t.labelNewEvent}

                                    />
                                    <span

                                    >
                                        {nameLength}/50
                                    </span>
                                </div>
                            ) : (
                                <div
                                    className="modal-info field"

                                >
                                    <p>{currentEvent.name}</p>
                                </div>
                            )}
                        </div>

                        <div className="form-item">
                            <label
                                className="form-lbl"

                            >
                                <EventCodeIcon />
                                {t.labelCode}
                            </label>
                            {mode !== 'view' ? (
                                <div>
                                    <input
                                        name="code"
                                        className="add-column"
                                        type="text"
                                        maxLength={50}
                                        value={currentEvent.code ?? ''}
                                        onChange={onChange}
                                        placeholder={t.labelEventCode}

                                    />
                                    <span

                                    >
                                        {codeLength}/50
                                    </span>
                                </div>
                            ) : (
                                <div
                                    className="modal-info field"

                                >
                                    <p>{currentEvent.code}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-item">
                        <label
                            className="form-lbl"

                        >
                            <DescriptionIcon />
                            {t.labelDescription}
                        </label>
                        {mode !== 'view' ? (
                            <textarea
                                name="description"
                                className="add-column"
                                rows={4}
                                value={currentEvent.description ?? ''}
                                onChange={onChange}
                                placeholder={t.labelEventDescription}

                            />
                        ) : (
                            <div
                                className="modal-info field"

                            >
                                <p>{currentEvent.description}</p>
                            </div>
                        )}
                    </div>

<div className="settings-header">
  <div className="form-item">
    <label
      className="form-lbl"

    >
      <EventTypeIcon />
      {t.labelType}
    </label>
    {mode !== 'view' ? (
      <Select
        id="event-type"
        options={eventTypeOptions}
        value={selectedEventTypeOption}
        onChange={selectedOption =>
          setCurrentEvent(prev => ({
            ...prev,
            type: selectedOption ? selectedOption.value : null
          }))
        }
        classNamePrefix="react-select"
      />
    ) : (
      <div
        className="modal-info field"

      >
        <p>{currentEvent.type}</p>
      </div>
    )}
  </div>

<div className="form-item">
  <label
    className="form-lbl"

  >
    <ColorIcon />
    {t.labelColor}
  </label>
  {mode !== 'view' ? (
    <ColorPicker
      id="event-color"
      color={currentEvent.color ?? 'rgb(0, 0, 0)'}
      setColor={newColor =>
        setCurrentEvent(prev => ({
          ...prev,
          color: newColor,
        }))
      }
    />
  ) : (
    <div
      className="color-picker"

    />
  )}
</div>

</div>


                    {[
                        { key: 'notification', label: t.labelNotification, Icon: NotificationIcon },
                        { key: 'condition', label: t.labelCondition, Icon: ConditionIcon },
                        { key: 'action', label: t.labelAction, Icon: ActionIcon }
                    ].map(({ key, label, Icon }) => (
                        <div key={key} className="form-item automation-row">
                            <div className="eds-row-left">
                                <div className="eds-icon-box">
                                    <Icon />
                                </div>
                                <span>{label}</span>
                            </div>

                            {mode !== 'view' ? (
                                <CustomSwitch
                                    checked={!!currentEvent[key]}
                                    onChange={(next) =>
                                        setCurrentEvent(prev => ({ ...prev, [key]: next ? 1 : 0 }))
                                    }
                                />
                            ) : (
                                <CustomSwitch checked={!!currentEvent[key]} disabled />
                            )}
                        </div>
                    ))}
                </div>

                <div
                    className="modal-footer"

                >
                    <button
                        className="btn btn-cancel"

                        onClick={close}
                    >
                        {t.buttonCancel}
                    </button>
                    {mode !== 'view' && (
                        <button
                            className="btn btn-apply"

                            onClick={() => {
                                setEvent(currentEvent);
                                close();
                            }}
                        >
                            {mode === 'add' ? t.buttonCreate : t.buttonSave}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
